/* eslint-disable @typescript-eslint/member-ordering */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { GSA } from '../models/gsa.model';
import { BillingRate } from '../models/pay-billing.model';
import { PremiumBillingRateRequest } from '../models/premium-billing-rate.model';
import { MarginCalculatorBaseService } from './margin-calculator-base.service';
import { MarginCalculatorPayBillingHelperService as helper } from './margin-calculator-pay-billing-helper.service';

export enum HoursFrequency {
    EMPTY = '-',
    WEEKLY = 'week',
    DAILY = 'day',
    TWO_WEEK = '2weeks'
}

export enum AndOr {
    EMPTY = '-',
    AND = 'and',
    OR = 'or'
}

@Injectable({
    providedIn: 'root'
})
export class MarginCalculatorBillingService extends MarginCalculatorBaseService {
    private _billingRates: BillingRate[] = null;
    private readonly _calculateShiftAmt = new Subject<void>();
    private readonly _triggerLockdownLogic = new Subject<void>();
    private readonly _removeLockdownLogic = new Subject<void>();
    private readonly _resetOverTimeRule = new Subject<boolean>();
    private readonly _stipendTaxable = new Subject<boolean>();
    private _primaryNonStandardBillingHours = 0;
    private _alternateNonStandardBillingHours = 0;
    private _primaryNonStandardOvertimeBillingHours = 0;
    private _alternateNonStandardOvertimeBillingHours = 0;

    constructor(private _http: HttpClient) {
        super();
    }

    set alternateNonStandardOvertimeBillingHours(hours: number) {
        this._alternateNonStandardOvertimeBillingHours = hours;
    }

    get alternateNonStandardOvertimeBillingHours(): number {
        return this._alternateNonStandardOvertimeBillingHours;
    }

    set primaryNonStandardOvertimeBillingHours(hours: number) {
        this._primaryNonStandardOvertimeBillingHours = hours;
    }

    get primaryNonStandardOvertimeBillingHours(): number {
        return this._primaryNonStandardOvertimeBillingHours;
    }

    set alternateNonStandardBillingHours(hours: number) {
        this._alternateNonStandardBillingHours = hours;
    }

    get alternateNonStandardBillingHours(): number {
        return this._alternateNonStandardBillingHours;
    }

    set primaryNonStandardBillingHours(hours: number) {
        this._primaryNonStandardBillingHours = hours;
    }

    get primaryNonStandardBillingHours(): number {
        return this._primaryNonStandardBillingHours;
    }

    removeLockdownLogic(): void {
        this._removeLockdownLogic.next();
    }

    get removeLockdownLogic$(): Observable<void> {
        return this._removeLockdownLogic.asObservable();
    }

    triggerLockdownLogic(): void {
        this._triggerLockdownLogic.next();
    }

    get triggerLockdownLogic$(): Observable<void> {
        return this._triggerLockdownLogic.asObservable();
    }

    calculateShiftRate(): void {
        this._calculateShiftAmt.next();
    }

    set stipendTaxable(stipendTaxable: boolean) {
        this._stipendTaxable.next(stipendTaxable);
    }

    getDisableFailSafe(billingRates?: BillingRate[]): boolean {
        if (this._billingRates !== null) {
            return this._billingRates[0]?.disableOtFailSafe ?? false;
        }
        return false;
    }

    resetOverTimeRule(billingRuleEnabled: boolean): void {
        this._resetOverTimeRule.next(billingRuleEnabled);
    }

    get resetOverTimeRule$(): Observable<boolean> {
        return this._resetOverTimeRule.asObservable();
    }

    /** @deprecated use helper version instead */
    getIncluded(type: string): boolean {
        return helper.getIncluded(type, (this._billingRates || [])[0]);
    }

    /** @deprecated use helper version instead */
    getMultiplier(type: string, shiftHours: number): string {
        return helper.getMultiplier(type, shiftHours, (this._billingRates || [])[0]);
    }

    /** @deprecated use helper version instead */
    getMultiplierRate(type: string, shiftHours: number): number {
        return helper.getMultiplierRate(type, shiftHours, (this._billingRates || [])[0]);
    }

    getHolidayMultiplier(shiftHours: number): string {
        const multiplier =
            this._billingRates && this._billingRates[0] ? this._billingRates[0][`holidayMultiplier${shiftHours}`] : 0;

        if (multiplier && multiplier !== '' && multiplier !== ' ') {
            return this._billingRates[0][`holidayMultiplier${shiftHours}`];
        } else {
            return this._billingRates && this._billingRates[0] ? this._billingRates[0].holidayMultiplierA : '';
        }
    }

    getBaseBillRate(shiftType: string, shiftHours: number): number {
        return MarginCalculatorBillingService.findBaseBillRate(shiftType, shiftHours, this._billingRates);
    }

    static findBaseBillRate(shiftType: string, shiftHours: number, billingRates: BillingRate[]): number {
        if (!Array.isArray(billingRates) || !billingRates.length) {
            return 0;
        }
        if (billingRates[0].isRangeBilling) {
            return billingRates[0].rangeBillingMin;
        }
        if (billingRates[0][`${shiftType}${shiftHours}`] as number) {
            return billingRates[0][`${shiftType}${shiftHours}`] as number;
        }
        if (billingRates[0][`${shiftType}All`] as number) {
            return billingRates[0][`${shiftType}All`] as number;
        }
        return billingRates[0].dayAll;
    }

    getOvertimeHoursRule(count: number, shiftHours: number): number {
        if (this._billingRates && this._billingRates.length > 0) {
            const shiftOvertimeRuleExists = helper.shiftOvertimeRuleExists(
                this._billingRates[0][`overTimeMultAmount${1}${shiftHours}`],
                this._billingRates[0][`overTimeHours${1}${shiftHours}`],
                this._billingRates[0][`overTimeFreq${1}${shiftHours}`]
            );

            let OvertimeHours =
                this._billingRates && this._billingRates[0] ? this._billingRates[0][`overTimeHours${count}A`] : 0;

            if (shiftOvertimeRuleExists) {
                OvertimeHours =
                    this._billingRates && this._billingRates[0]
                        ? this._billingRates[0][`overTimeHours${count}${shiftHours}`]
                        : null;
            }
            return OvertimeHours;
        }
    }

    getOvertimeHoursFrequency(count: number, shiftHours: number): string {
        if (this._billingRates && this._billingRates.length > 0) {
            const shiftOvertimeRuleExists = helper.shiftOvertimeRuleExists(
                this._billingRates[0][`overTimeMultAmount${1}${shiftHours}`],
                this._billingRates[0][`overTimeHours${1}${shiftHours}`],
                this._billingRates[0][`overTimeFreq${1}${shiftHours}`]
            );

            let overtimeFreq =
                this._billingRates && this._billingRates[0]
                    ? this._billingRates[0][`overTimeFreq${count}A`]
                    : HoursFrequency.EMPTY;

            if (shiftOvertimeRuleExists) {
                overtimeFreq =
                    this._billingRates && this._billingRates[0]
                        ? this._billingRates[0][`overTimeFreq${count}${shiftHours}`] &&
                          this._billingRates[0][`overTimeFreq${count}${shiftHours}`] !== ''
                            ? this._billingRates[0][`overTimeFreq${count}${shiftHours}`]
                            : HoursFrequency.EMPTY
                        : HoursFrequency.EMPTY;
            }
            return overtimeFreq;
        }
    }

    shiftOvertimeRuleExists(multiplier: string, hours1: number, OverTimeFreq1: string): boolean {
        const exists: boolean =
            (multiplier && multiplier.trim() !== '') ||
            (OverTimeFreq1 && OverTimeFreq1.trim() !== '') ||
            (hours1 && hours1 > 0);
        return exists;
    }

    getHourCount(type: string, count: number, shiftHours: number, billingRates?: BillingRate[]): string {
        const hourCount =
            this._billingRates && this._billingRates[0]
                ? this._billingRates[0][`${type}HourCount${count}${shiftHours}`]
                : null;

        if (hourCount && this.isValidRule(type, shiftHours)) {
            return hourCount;
        } else {
            return this._billingRates && this._billingRates[0]
                ? this._billingRates[0][`${type}HourCount${count}A`]
                : null;
        }
    }

    getOvertimeAndOrRule(shiftHours: number): string {
        if (this._billingRates && this._billingRates.length > 0) {
            const shiftOvertimeRuleExists = helper.shiftOvertimeRuleExists(
                this._billingRates[0][`overTimeMultAmount${1}${shiftHours}`],
                this._billingRates[0][`overTimeHours${1}${shiftHours}`],
                this._billingRates[0][`overTimeFreq${1}${shiftHours}`]
            );

            let overtimeAndOr = this._billingRates && this._billingRates[0] ? this._billingRates[0].overTimeAndOrA : '';

            const andOr =
                this._billingRates && this._billingRates[0] ? this._billingRates[0][`overTimeAndOr${shiftHours}`] : '';

            if (shiftOvertimeRuleExists) {
                overtimeAndOr = andOr && andOr !== '' ? andOr : HoursFrequency.EMPTY;
            }
            return overtimeAndOr;
        }
    }

    haveMultipleRules(type: string, shiftHours?: number, billingRates?: BillingRate[]): boolean {
        const andOr =
            this._billingRates && this._billingRates[0] ? this._billingRates[0][`${type}AndOr${shiftHours}`] : null;

        if (shiftHours && andOr) {
            return this._billingRates[0][`${type}AndOr${shiftHours}`] !== '';
        } else {
            return !andOr && this._billingRates && this._billingRates[0]
                ? this._billingRates[0][`${type}AndOrA`] !== ''
                : false;
        }
    }

    getBillingRates(
        hospitalId: number,
        professionId: number,
        expertiseId: number,
        weeks: number,
        isRapidResponse: boolean
    ): Observable<BillingRate[]> {
        if (
            hospitalId !== undefined &&
            professionId !== undefined &&
            expertiseId !== undefined &&
            weeks !== undefined
        ) {
            return this._http
                .get<
                    BillingRate[]
                >(`${this.coreApiPath}/margin-calculator/billingrates?hospitalId=${hospitalId}&professionId=${professionId}&expertiseId=${expertiseId}&weeks=${weeks}&isRapidResponse=${isRapidResponse}`)
                .pipe(
                    map((billingRates) => {
                        return (this._billingRates = billingRates);
                    })
                );
        }
        return of(undefined);
    }

    getPremiumBillingRate(request: PremiumBillingRateRequest): Observable<number> {
        return this._http.get<number>(
            `${this.coreApiPath}/margin-calculator/premium-bill-rate?state=${request.state}&otWeekOrDayFirst=${request.otWeekOrDayFirst}&andOrFirst=${request.andOrFirst}&ruleHoursFirst=${request.ruleHoursFirst}&otWeekOrDaySecond=${request.otWeekOrDaySecond}&ruleHoursSecond=${request.ruleHoursSecond}&baseBillRate=${request.baseBillRate}&overtimeBillRate=${request.overtimeBillRate}&shiftLength=${request.shiftLength}&shiftsPerWeek=${request.shiftsPerWeek}`
        );
    }

    private isValidRule(type: string, shiftHours: number, billingRates?: BillingRate[]) {
        //check multiplier exists for this rule or it is invalid and should use 'A'
        const multiplier =
            this._billingRates && this._billingRates[0]
                ? this._billingRates[0][`${type}Multiplier${shiftHours}`]
                : null;
        return multiplier && multiplier !== ' ';
    }

    getMaxStipendFromGsaData(isHousingSelected: boolean, gsaData: GSA): number {
        if (isHousingSelected) {
            return gsaData ? gsaData.mealRatePerDay * 7 : 0;
        } else {
            return gsaData ? gsaData.weeklyStipendMax : 0;
        }
    }
}
